import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, CheckCircle2, Clock, Archive, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api, formatApiError } from "@/lib/api";

const STEPS = ["Demande envoyée", "En cours de traitement", "Traitée"];
const stepIndex = (status) => (status === "traité" || status === "archivé" ? 2 : 0);

export default function SuiviDevis() {
  const [params] = useSearchParams();
  const [ref, setRef] = useState(params.get("ref") || "");
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const lookup = async (r) => {
    if (!r.trim()) return;
    setLoading(true);
    setError("");
    setQuote(null);
    try {
      const { data } = await api.get(`/quotes/track/${r.trim()}`);
      setQuote(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const initial = params.get("ref");
    if (initial) lookup(initial);
  }, []);

  const submit = (e) => {
    e.preventDefault();
    lookup(ref);
  };

  const active = quote ? stepIndex(quote.status) : 0;

  return (
    <div data-testid="suivi-devis-page" className="pt-[148px]">
      <section className="max-w-[900px] mx-auto px-5 md:px-10 py-16 md:py-24">
        <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>Suivi</p></Reveal>
        <Reveal delay={0.05}>
          <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tight leading-none">Suivre ma demande de devis</h1>
          <p className="mt-6 text-lg max-w-2xl" style={{ color: "var(--ink-2)" }}>
            Saisissez la référence communiquée lors de l'envoi de votre demande pour connaître son avancement.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} className="mt-10 flex flex-col sm:flex-row gap-3" data-testid="track-form">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--ink-2)" }} />
              <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Référence de votre demande (ex : 3f2a8c1d-…)"
                className="field" style={{ paddingLeft: "2.5rem" }} data-testid="track-ref-input" />
            </div>
            <button type="submit" disabled={loading} className="btn-primary px-8 py-3 font-semibold flex items-center justify-center gap-2" data-testid="track-submit-btn">
              {loading ? <Loader2 size={16} className="animate-spin" /> : null} Suivre
            </button>
          </form>
        </Reveal>

        {error && (
          <p className="mt-8 p-4 text-sm font-medium" style={{ background: "var(--white)", border: "1px solid var(--orange)", color: "var(--orange)" }} data-testid="track-error">
            {error}
          </p>
        )}

        {quote && (
          <div className="mt-10 p-8 md:p-10" style={{ background: "var(--white)", border: "1px solid var(--border)" }} data-testid="track-result">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
              <div>
                <p className="overline mb-2" style={{ color: "var(--orange)" }}>Demande de devis</p>
                <h2 className="font-display font-bold text-2xl">{quote.subject}</h2>
                <p className="mt-2 text-sm" style={{ color: "var(--ink-2)" }}>
                  Déposée le {(quote.created_at || "").slice(0, 10)} par {quote.name} · Réf. <span className="font-mono2">{quote.id}</span>
                </p>
              </div>
              <span className="overline px-3 py-1 h-fit whitespace-nowrap" data-testid="track-status-badge"
                style={{ background: quote.status === "nouveau" ? "var(--orange)" : "var(--ink)", color: "var(--bone)" }}>
                {quote.status === "nouveau" ? "Reçue" : quote.status === "traité" ? "Traitée" : "Archivée"}
              </span>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-2" data-testid="track-timeline">
              {STEPS.map((s, i) => {
                const done = i <= active;
                const Icon = i === 2 && quote.status === "archivé" ? Archive : done ? CheckCircle2 : Clock;
                return (
                  <div key={s} className="flex flex-col items-center text-center gap-3">
                    <div className="w-full h-1" style={{ background: done ? "var(--orange)" : "var(--border)" }} />
                    <Icon size={22} style={{ color: done ? "var(--orange)" : "var(--ink-2)" }} />
                    <p className="text-xs sm:text-sm font-semibold" style={{ color: done ? "var(--ink)" : "var(--ink-2)" }}>{s}</p>
                  </div>
                );
              })}
            </div>

            <p className="mt-10 text-sm" style={{ color: "var(--ink-2)" }}>
              {quote.status === "nouveau"
                ? "Votre demande a bien été reçue. Notre équipe l'analyse et vous répond sous 48h ouvrées."
                : "Votre demande a été traitée. Une réponse vous a été adressée par email — pensez à vérifier vos spams."}
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
